import { useCallback, useState } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Icon from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';
import ActionButtons from 'fe-design-base/molecules/ActionButtons';
import { ActionButtonProps } from 'fe-design-base/molecules/ActionButtons/ActionButtons';
import Skeleton from 'fe-design-base/molecules/Loaders/Skeleton';

import { JobRequestEligibleBoost } from 'features/hiringV2/interfaces/IJobRequestBoost';

import { EVENT_CATEGORIES, PRODUCT_AREAS } from 'util/tracking_constants';
import { useExtendUx, withUxRoot } from 'util/uxEvents';

import { formatPartnerName } from './helpers';
import TransactionWidget, { PartnerWidget } from './TransactionWidget';

const BoostModalLoadingState = () => (
  <Box column gap={16} w="100%" borderbox pv={24}>
    <Box
      column
      gap={16}
      p={32}
      w="100%"
      bradiusm
      bw={1}
      bcolor="mono300"
      borderbox
    >
      <Skeleton height={12} width={200} bradiusm />
      <Skeleton height={12} width={200} bradiusm />
    </Box>
    <Box
      column
      gap={16}
      p={32}
      w="100%"
      bradiusm
      bw={1}
      bcolor="mono300"
      borderbox
    >
      <Skeleton height={12} width={200} bradiusm />
      <Skeleton height={12} width={200} bradiusm />
    </Box>
  </Box>
);

interface BoostModalProps {
  eligiblePartners: string[];
  partnerWidgets: Record<string, PartnerWidget>;
  eligibleBoosts: JobRequestEligibleBoost[];
  handleOpenBillingModal: (partnerName: string) => void;
  skipApiCall?: boolean;
  isLoading?: boolean;
}

function BoostModal({
  isLoading,
  eligiblePartners,
  partnerWidgets,
  eligibleBoosts,
  handleOpenBillingModal,
}: BoostModalProps) {
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const extendUx = useExtendUx();
  extendUx({ partner: selectedPartner });

  const handleClickBoost = useCallback(() => {
    handleOpenBillingModal(selectedPartner || '');
  }, [handleOpenBillingModal, selectedPartner]);

  const dialogButtons: ActionButtonProps[] = [
    {
      label: selectedPartner
        ? `Boost on ${formatPartnerName(selectedPartner)} for $${
            partnerWidgets[selectedPartner]?.price
          }`
        : `Boost job`,
      variant: 'primary',
      disabled: !selectedPartner,
      onClick: handleClickBoost,
      uxElement: 'boost-button',
    },
  ];

  return (
    <>
      <Text i18n="hiring.boost_upsell_modal.sub_title" color="mono900" />
      <Box>
        <Box flex column gap={16} alignItemsCenter mt={16}>
          {isLoading ? (
            <BoostModalLoadingState />
          ) : (
            eligiblePartners?.map((partner, key) => {
              const partnerWidget = partnerWidgets[partner];
              const { show, name, ...props } = partnerWidget || {};
              const boosted = !!eligibleBoosts.find(
                eligiblePartner => eligiblePartner.name === name
              )?.isBoosted;

              return (
                <Box key={key} w="100%">
                  <TransactionWidget
                    key={key}
                    {...props}
                    partnerName={name}
                    boosted={boosted}
                    onClick={setSelectedPartner}
                    selected={selectedPartner}
                    leftContent={
                      boosted ? (
                        <Icon id="boosted" iconName="Celebrate" />
                      ) : null
                    }
                  />
                </Box>
              );
            })
          )}
        </Box>
        <Box mt={36}>
          <ActionButtons buttons={dialogButtons} />
        </Box>
      </Box>
    </>
  );
}

export default withUxRoot({
  productArea: PRODUCT_AREAS.HIRING,
  eventCategory: EVENT_CATEGORIES.HIRING_DASHBOARD,
})(BoostModal);
